import type { ReactNode } from 'react';
import type { Baby } from '@shared/types.ts';
import { LionMark } from './LionMark.tsx';

type Props = {
  title: ReactNode;
  subtitle?: ReactNode;
  baby?: Baby | null;
  rightSlot?: ReactNode;
  showAvatar?: boolean;
};

/** Round 38×38 accent button — used by the "Add measurement / milestone"
 *  affordances on growth, milestones, and home headers. */
export function HeaderIconButton({
  ariaLabel,
  onClick,
  children,
  accent = 'var(--accent)',
  ink = 'var(--accent-ink)',
}: {
  ariaLabel: string;
  onClick: () => void;
  children: ReactNode;
  accent?: string;
  ink?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: 38,
        height: 38,
        borderRadius: 999,
        background: accent,
        color: ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

export function HeaderBar({ title, subtitle, baby, rightSlot, showAvatar = true }: Props) {
  const isLion = baby && baby.name.toLowerCase() === 'lion';
  return (
    <div className="lt-headerbar">
      {showAvatar && baby && (
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 999,
            background: baby.color ?? 'var(--accent)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Newsreader, serif',
            fontSize: 19,
            fontWeight: 500,
            boxShadow: '0 1px 3px rgba(60,40,25,0.15)',
            flexShrink: 0,
          }}
        >
          {isLion ? <LionMark size={26} color="#fff" /> : baby.name[0]?.toUpperCase()}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="lt-serif"
          style={{
            fontSize: 22,
            fontWeight: 500,
            letterSpacing: -0.015,
            color: 'var(--text-1)',
            lineHeight: 1.1,
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2, letterSpacing: -0.005 }}>
            {subtitle}
          </div>
        )}
      </div>
      {rightSlot}
    </div>
  );
}
